import React, {memo, useCallback, useEffect, useMemo, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {useMapSelector} from '../../../../hooks/map-selector';
import {SelectItem} from '../../../../components/Select';
import {useQuestion} from '../../../../hooks/question';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {useHistory, useParams} from 'react-router-dom';
import {useSelector} from 'react-redux';
import {ControlModel, InputModel, MediaModel} from '../../../../types/control';
import {HomepageItemModel, HomepageItemType, ScenarioModel} from '../../../../store/control/state';
import {TYPE_NAME_MAP} from '../HomepageListScreen';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {useMap} from '../../../../hooks/map';
import {useSetSelector} from '../../../../hooks/set-selector';

const BACK_URL = '/settings/homepage';

const TYPE_OPTION_LIST: SelectItem<HomepageItemType>[] =
    (Object.entries(TYPE_NAME_MAP) as [HomepageItemType, string][])
        .map(([type, name]) => ({label: name, value: type}));

export const HomepageEditScreen = memo(function HomepageEditScreen() {
    const params = useParams<{ id: string }>();
    const question = useQuestion();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();

    const homepageItemList = useSelector(state => state.control.homepageItemList);
    const inputTypeSet = useSetSelector(state => state.control.inputTypeList);
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);
    const controlOptionList = useMap(useFilterSelector(
        state => state.control.controlList,
        control => controlTypeSet[control.type_id].show_on_homepage,
        [controlTypeSet],
        ),
        control => ({value: control, label: control.name}),
        [],
    );
    const scenarioOptionList = useMapSelector(
        state => state.control.scenarioList,
        scenario => ({value: scenario, label: scenario.name}),
        [],
    );
    const sensorOptionList = useMap(
        useFilterSelector(
            state => state.control.inputList,
            input => inputTypeSet[input.type_id]?.primitive_name === 'sensor',
        ),
        sensor => ({value: sensor, label: sensor.name}),
    );
    const mediaOptionList = useMapSelector(
        state => state.control.mediaList,
        media => ({value: media, label: media.name}),
        [],
    );

    const [selectedHomepageItem, setSelectedHomepageItem] = useState<HomepageItemModel>(null);
    const [selectedType, setSelectedType] = useState<SelectItem<HomepageItemType>>(null);
    const [selectedControlOption, setSelectedControlOption] = useState<SelectItem<ControlModel>>(null);
    const [selectedScenarioOption, setSelectedScenarioOption] = useState<SelectItem<ScenarioModel>>(null);
    const [selectedSensorOption, setSelectedSensorOption] = useState<SelectItem<InputModel>>(null);
    const [selectedMediaOption, setSelectedMediaOption] = useState<SelectItem<MediaModel>>(null);

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const onDeletePressed = useCallback(() => {
        question('Silmek istediğinizden emin misiniz?', () => {
            return rpc.deleteHomepageItem(selectedHomepageItem.id)
                .then(() => {
                    success('Başarılı bir şekilde silindi.');
                    onBackPressed();
                })
                .catch(failure);
        });
    }, [
        failure,
        onBackPressed,
        question,
        rpc,
        selectedHomepageItem?.id,
        success,
    ]);

    const isFieldsValid = useCallback((): boolean => {
        if (selectedType == null) {
            failure('Lütfen geçerli bir tip seçiniz.');

            return false;
        }

        if (selectedType.value === 'control' && selectedControlOption == null) {
            failure('Lütfen geçerli bir kontrol seçiniz.');

            return false;
        }

        if (selectedType.value === 'scenario' && selectedScenarioOption == null) {
            failure('Lütfen geçerli bir senaryo seçiniz.');

            return false;
        }

        if (selectedType.value === 'sensor' && selectedSensorOption == null) {
            failure('Lütfen geçerli bir sensör seçiniz.');

            return false;
        }

        if (selectedType.value === 'media' && selectedMediaOption == null) {
            failure('Lütfen geçerli bir kumanda seçiniz.');

            return false;
        }

        return true;
    }, [
        failure,
        selectedType,
        selectedControlOption,
        selectedScenarioOption,
        selectedSensorOption,
        selectedMediaOption,
    ]);

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        const order = homepageItemList.length === 0 ? 0 :
            homepageItemList.sort((a, b) => b.order - a.order)[0].order + 1;

        const homepageItemModel: HomepageItemModel = {
            id: selectedHomepageItem?.id || null,
            relational_id: selectedType.value === 'control' ? selectedControlOption.value.id :
                selectedType.value === 'scenario' ? selectedScenarioOption.value.id :
                    selectedType.value === 'sensor' ? selectedSensorOption.value.id :
                        selectedType.value === 'media' ? selectedMediaOption.value.id :
                            null,
            type: selectedType.value,
            order,
        };

        return rpc.updateHomepageItem(homepageItemModel)
            .then(() => {
                success('Başarılı bir şekilde ' + (homepageItemModel.id ? 'düzenlendi.' : 'oluşturuldu.'));
                onBackPressed();
            }).catch(failure);
    }, [
        homepageItemList,
        failure,
        isFieldsValid,
        onBackPressed,
        rpc,
        selectedControlOption?.value?.id,
        selectedHomepageItem?.id,
        selectedSensorOption?.value?.id,
        selectedScenarioOption?.value?.id,
        selectedMediaOption?.value?.id,
        selectedType?.value,
        success,
    ]);

    const buttons = useMemo(() => [
        {
            text: 'SİL',
            className: 'mulberry-wood',
            onClick: onDeletePressed,
            hidden: !(selectedHomepageItem),
        },
        {
            text: 'KAYDET',
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: 'İPTAL',
            className: 'red',
            url: BACK_URL,
        },
    ], [onCreatePressed, onDeletePressed, selectedHomepageItem]);

    useEffect(() => {
        if (params.id == null) {
            return;
        }

        const item = homepageItemList.find(homepageItem => homepageItem.id === params['id']);
        if (item == null) {
            return;
        }

        setSelectedHomepageItem(item);
        setSelectedType(TYPE_OPTION_LIST.find(typeOption => typeOption.value === item.type));
        const selectedControl = item.type === 'control' &&
            controlOptionList.find(controlOption => controlOption.value.id === item.relational_id);
        setSelectedControlOption(selectedControl || controlOptionList[0] || null);
        const selectedScenario = item.type === 'scenario' &&
            scenarioOptionList.find(scenarioOption => scenarioOption.value.id === item.relational_id);
        setSelectedScenarioOption(selectedScenario || scenarioOptionList[0] || null);
        const selectedSensor = item.type === 'sensor' &&
            sensorOptionList.find(sensorOption => sensorOption.value.id === item.relational_id);
        setSelectedSensorOption(selectedSensor || sensorOptionList[0] || null);
        const selectedMedia = item.type === 'media' &&
            mediaOptionList.find(mediaOption => mediaOption.value.id === item.relational_id);
        setSelectedMediaOption(selectedMedia);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (params.id != null) {
            return;
        }

        setSelectedType(TYPE_OPTION_LIST[0]);
        setSelectedControlOption(controlOptionList[0] || null);
        setSelectedScenarioOption(scenarioOptionList[0] || null);
        setSelectedSensorOption(sensorOptionList[0] || null);
        setSelectedMediaOption(mediaOptionList[0] || null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <PasswordContainer requiredRole='admin' isFullscreen={true}>
            <SettingsPageContainer
                buttons={buttons}
                className='service-edit'
                backUrl={BACK_URL}>
                <div className='content-divider'>
                    <div className='content-wrapper'>
                        <SettingSelectComponent
                            label='Tip'
                            options={TYPE_OPTION_LIST}
                            onChange={setSelectedType}
                            value={selectedType}/>
                        {
                            renderIf(selectedType?.value === 'control')(() =>
                                <SettingSelectComponent
                                    label={selectedType?.label}
                                    options={controlOptionList}
                                    onChange={setSelectedControlOption}
                                    value={selectedControlOption}/>,
                            )
                        }
                        {
                            renderIf(selectedType?.value === 'scenario')(() =>
                                <SettingSelectComponent
                                    label={selectedType?.label}
                                    options={scenarioOptionList}
                                    onChange={setSelectedScenarioOption}
                                    value={selectedScenarioOption}/>,
                            )
                        }
                        {
                            renderIf(selectedType?.value === 'sensor')(() =>
                                <SettingSelectComponent
                                    label={selectedType?.label}
                                    options={sensorOptionList}
                                    onChange={setSelectedSensorOption}
                                    value={selectedSensorOption}/>,
                            )
                        }
                        {
                            renderIf(selectedType?.value === 'media')(() =>
                                <SettingSelectComponent
                                    label={selectedType?.label}
                                    options={mediaOptionList}
                                    onChange={setSelectedMediaOption}
                                    value={selectedMediaOption}/>,
                            )
                        }
                    </div>
                    <div className='content-wrapper'>

                    </div>
                </div>
            </SettingsPageContainer>
        </PasswordContainer>
    );
});
